import {
    createPromoTileComponent,
    IPromoTileProperties,
    PromoTile,
} from '@vfde-brix/promo-tile';
import { formValidation, NO_PATTERN_BUSINESS_LOGIC, required } from '@vfde-brix/core';
import { HARDWARE_TRADE_IN_PROMO_TILE, SUGGEST_FIELD_INPUT_THROTTLE_DURATION, TAUSCHBONUS_PROMO_TILE, TRADEIN_SUGGEST_FIELD_ID } from '../container/TradeIn/constants';
import { FORM_TEXT_INPUT_BASE_CLASSNAME, IFormTextInputProperties } from '@vfde-brix/form-text-input';
import { ISuggestFieldProperties } from '@vfde-brix/suggest-field';
import { INotificationProperties } from '@vfde-brix/notification';
import { IActions, StateProps } from 'app/container/TradeIn/interface';
import formatAndSortTradeInPayload from 'Helper/formatAndSortTradeInPayload';
import replaceCMSPlaceholder from 'Helper/replaceCMSPlaceholder';

/**
 * Mount trade in promo tile
 * @param actions Actions object
 */
export const mountTradeInPromoTile = ({ getTradeInDevices, setSuggestFieldValue, setSelectedTradeInDeviceId }: IActions): PromoTile => {
    const container = document.getElementById(HARDWARE_TRADE_IN_PROMO_TILE);
    const tradeInOptions = (window as any).additionalPageOptions?.promos?.tradeIn;
    let timeoutId = null;
    const onInput = (event: InputEvent) => {
        const target = event.target as HTMLInputElement;

        setSuggestFieldValue(target.value);
        timeoutId && clearTimeout(timeoutId);

        timeoutId = setTimeout(() => {
            getTradeInDevices();
        }, SUGGEST_FIELD_INPUT_THROTTLE_DURATION);
    };

    const suggestFieldProperties: ISuggestFieldProperties = {
        disableFiltering: true,
        business: {
            onSelect: (deviceId) => {
                if (!deviceId) {
                    return;
                }

                setSelectedTradeInDeviceId(deviceId);
            },
        },
        inputField: {
            containerInput: {
                stdId: TRADEIN_SUGGEST_FIELD_ID,
                stdName: TRADEIN_SUGGEST_FIELD_ID,
                optType: 'text',
                stdValue: '',
                optAutoCompleteOff: true,
                optSpacingRight: true,
                business: NO_PATTERN_BUSINESS_LOGIC,
            },
            optInputFieldType: FORM_TEXT_INPUT_BASE_CLASSNAME,
            containerLabel: {
                stdFor: TRADEIN_SUGGEST_FIELD_ID,
                txtText: tradeInOptions?.suggestFieldLabel || '',
                business: NO_PATTERN_BUSINESS_LOGIC,
            },
            business: {
                // TODO: make on change optional in brix if it's possible
                onChange: () => null,
                onInput,
                validation: [
                    {
                        validator: required,
                        errorKey: 'tradeInDeviceRequired',
                    },
                ],
            },
        },
        loadingAnimation: {
            size: 'medium',
            business: NO_PATTERN_BUSINESS_LOGIC,
        },
    };

    const promoTileComponent = container && createPromoTileComponent(container, NO_PATTERN_BUSINESS_LOGIC);
    const promoTileProperties = promoTileComponent.getProperties();

    const promoTileWithSuggestField = container && createPromoTileComponent(container, {
        ...promoTileProperties,
        suggestField: suggestFieldProperties,
    });

    initInputFieldValidation(promoTileWithSuggestField);

    return promoTileWithSuggestField;
};

/**
 * Update promo tile
 * @param promoTile PromoTile component
 * @param state State
 * @param preventRerender Boolean to prevent rerendering of promotile and suggest field.
 * Instead the update will be done in the `didReceiveProps` method of the component
 */
export const updateTradeInPromoTile = (promoTile: PromoTile, state: StateProps, preventRerender: boolean) => {
    const properties = promoTile?.getProperties();
    const tradeInOptions = (window as any).additionalPageOptions?.promos?.tradeIn;

    if (properties.suggestField === undefined) {
        return;
    }

    const fullResult = formatAndSortTradeInPayload(state.payload);

    const notificationProperties: Partial<INotificationProperties> = {
        optHidden: !state.tradeInSwitch || !(state.hasError || state.isDeviceNotFound),
    };

    if (state.hasError) {
        // show API error notification
        notificationProperties.optType = 'error';
        notificationProperties.txtContent = tradeInOptions?.technicalErrorText || '';
    } else if (state.isDeviceNotFound) {
        // show 'device not found' notification
        notificationProperties.optType = 'info';
        notificationProperties.txtContent = tradeInOptions?.deviceNotFoundText || '';
    }

    const synchronousInputFieldProperties = promoTile.getSuggestFiledComponent().getInputFieldComponent().getProperties();

    const updatedProperties: IPromoTileProperties = {
        ...properties,
        suggestField: {
            ...properties.suggestField,
            fullResult,
            inputField: {
                ...synchronousInputFieldProperties,
                business: {
                    ...synchronousInputFieldProperties.business,
                    onInput: properties.suggestField.inputField.business.onInput, // we need to use the onInput handler of the promoTile properties, otherwise we will get an 'Maximum call stack size exceeded' error since the SuggestField registers an onInput callback which calls `handleOnInput` which again calls the `onInput` callback (infinite loop)
                },
                stdErrorKey: '',
                stdErrorMessage: '',
                containerInput: {
                    ...properties.suggestField.inputField.containerInput,
                    stdValue: (properties.suggestField.inputField.containerInput as IFormTextInputProperties).stdValue,
                } as IFormTextInputProperties,
            },
        },
        notification: {
            ...properties.notification,
            ...notificationProperties,
        },
        preventRerenderSuggestField: preventRerender,
    };

    promoTile.update(updatedProperties, preventRerender);

    if (!preventRerender) {
        // here we need to initialize the input field validation again
        // otherwise validation won't work anymore because after a full re-rendering
        // the HTML element will be a different one than from the initial mounting
        initInputFieldValidation(promoTile);
    }
};

/**
 * Initializes the inputField validation.
 * @param promoTile The promoTile component
 */
const initInputFieldValidation = (promoTile: PromoTile) => {
    const form = {
        formFields: [
            promoTile.getSuggestFiledComponent().getInputFieldComponent(),
        ],
    };

    formValidation(form);
};

/**
 * Mount trade in promo tile
 * @param actions Actions object
 */
export const mountTauschbonusPromoTile = (container, tauschbonusOptions) => {
    const promoTileComponent = container && createPromoTileComponent(container, NO_PATTERN_BUSINESS_LOGIC);
    const promoTileProperties = promoTileComponent.getProperties();

    const stdHeadline = replaceCMSPlaceholder(promoTileProperties.stdHeadline || '', { tauschbonusValue: tauschbonusOptions.tauschbonusValue });

    const promoTileWithHeadline = container && createPromoTileComponent(container, {
        ...promoTileProperties,
        stdHeadline,
    });
};
